import * as R from 'remeda';
import { useMutation } from '@tanstack/react-query';
import { isFileUIPart, isTextUIPart, isToolUIPart } from 'ai';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { useCopyToClipboard } from '@rossum/components/copy-to-clipboard/useCopyToClipboard';
import {
  IconCheck,
  IconCopy,
  IconFile,
  IconThumbDown,
  IconThumbDownFilled,
  IconThumbUp,
  IconThumbUpFilled,
} from '@rossum/ui/icons/tabler';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  keyframes,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { borderRadius } from '@rossum/ui/theme';
import { useChatStore } from '../hooks/chatStore';
import { ChatUIMessage, getChatMetadata } from '../hooks/UseAgentChat/schemas';
import { downloadChatFile } from '../hooks/useChatApi';
import { ChatBubble } from '../shared/ChatBubble';
import { triggerBlobDownload } from '../utils/download';
import {
  getFinalTextContent,
  getReasoningContent,
  hasRedactedReasoning,
} from '../utils/message';
import { getAgentActivity } from './agentActivity';
import { AgentActivityLabel } from './AgentActivityLabel';
import { ChatMessageText } from './ChatMessageText';
import { ChatStepIndicator } from './ChatStepIndicator';
import { CollapsibleReasoning } from './CollapsibleReasoning';
import { MarkdownRenderer } from './MarkdownRenderer';

const blink = keyframes`
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
`;

const TypingCursor = () => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      width: '2px',
      height: '1em',
      bgcolor: 'text.primary',
      ml: 0.25,
      verticalAlign: 'text-bottom',
      animation: `${blink} 1s step-end infinite`,
    }}
  />
);

const processMermaidBlocks = (
  content: string
): { filtered: string; hasIncomplete: boolean } => {
  const mermaidStartRegex = /```mermaid\n/g;
  const result: string[] = [];
  let lastIndex = 0;
  let hasIncomplete = false;
  let match: RegExpExecArray | null;

  // oxlint-disable-next-line no-cond-assign
  while ((match = mermaidStartRegex.exec(content)) !== null) {
    result.push(content.slice(lastIndex, match.index));

    const afterStart = match.index + match[0].length;
    const closingIndex = content.indexOf('\n```', afterStart);

    if (closingIndex !== -1) {
      const blockEnd = closingIndex + 4;
      result.push(content.slice(match.index, blockEnd));
      lastIndex = blockEnd;
    } else {
      hasIncomplete = true;
      lastIndex = content.length;
      break;
    }
  }

  if (lastIndex < content.length) {
    result.push(content.slice(lastIndex));
  }

  return { filtered: result.join(''), hasIncomplete };
};

type FeedbackButtonProps = {
  isPositive: boolean;
  isSelected: boolean;
  onClick: () => void;
};

const FeedbackButton = ({
  isPositive,
  isSelected,
  onClick,
}: FeedbackButtonProps) => {
  const intl = useIntl();
  const FilledIcon = isPositive ? IconThumbUpFilled : IconThumbDownFilled;
  const OutlineIcon = isPositive ? IconThumbUp : IconThumbDown;

  return (
    <IconButton
      data-cy={isPositive ? 'chat-feedback-positive' : 'chat-feedback-negative'}
      size="small"
      color="secondary"
      aria-label={intl.formatMessage({
        id: isPositive
          ? 'features.chat.feedback.thumbsUp'
          : 'features.chat.feedback.thumbsDown',
      })}
      aria-pressed={isSelected}
      onClick={onClick}
      sx={{ p: 0.5 }}
    >
      <SvgIcon
        sx={{
          fontSize: 16,
          color: isSelected ? 'primary.main' : 'text.secondary',
        }}
      >
        {isSelected ? <FilledIcon /> : <OutlineIcon />}
      </SvgIcon>
    </IconButton>
  );
};

type CopyButtonProps = {
  content: string;
};

const CopyButton = ({ content }: CopyButtonProps) => {
  const intl = useIntl();
  const { copied, copyToClipboard } = useCopyToClipboard(content);

  return (
    <Tooltip
      title={intl.formatMessage({
        id: copied ? 'features.chat.copy.copied' : 'features.chat.copy.copy',
      })}
      placement="top"
    >
      <IconButton
        data-cy="chat-copy-message-button"
        size="small"
        color="secondary"
        aria-label={intl.formatMessage({
          id: copied ? 'features.chat.copy.copied' : 'features.chat.copy.copy',
        })}
        onClick={copyToClipboard}
        sx={{ p: 0.5 }}
      >
        <SvgIcon sx={{ fontSize: 16, color: 'text.secondary' }}>
          {copied ? <IconCheck /> : <IconCopy />}
        </SvgIcon>
      </IconButton>
    </Tooltip>
  );
};

/**
 * Prefix the agent's attachment processor stamps on a file part whose bytes live
 * in the chat workspace rather than inline in the message (see mr-fabry's
 * `shared/attachments.ts`). Such a part's `url` is `file-ws-<path>` — not a real
 * URL — so it renders as a download chip, not an `<img>`. Keep in sync with the
 * backend marker.
 */
const WORKSPACE_FILE_PREFIX = 'file-ws-';

const isWorkspaceFilePart = (url: string): boolean =>
  url.startsWith(WORKSPACE_FILE_PREFIX);

type WorkspaceFileChipProps = {
  /** The workspace-relative path to download, e.g. `report-3f9a21c8.pdf`. */
  path: string;
};

const WorkspaceFileChip = ({ path }: WorkspaceFileChipProps) => {
  const intl = useIntl();
  const chatId = useChatStore(s => s.drawerChatId);

  const { mutate, isLoading } = useMutation({
    mutationFn: () => {
      if (!chatId) {
        throw new Error('No active chat to download from');
      }
      return downloadChatFile(chatId, path);
    },
    onSuccess: blob => triggerBlobDownload(path, blob),
    onError: () => {
      toast.error(
        intl.formatMessage(
          { id: 'features.chat.files.downloadFailed' },
          { filename: path }
        )
      );
    },
  });

  return (
    <Chip
      data-cy="chat-attachment-download"
      icon={
        isLoading ? (
          <CircularProgress size={14} />
        ) : (
          <SvgIcon>
            <IconFile />
          </SvgIcon>
        )
      }
      label={path}
      onClick={() => mutate()}
      disabled={isLoading || !chatId}
      clickable
      size="small"
      color="transparent"
      sx={{ maxWidth: 240 }}
    />
  );
};

type UserMessageProps = {
  message: ChatUIMessage;
};

const UserMessage = ({ message }: UserMessageProps) => {
  const intl = useIntl();
  const fileParts = message.parts.filter(isFileUIPart);

  const [workspaceFileParts, inlineFileParts] = R.partition(fileParts, part =>
    isWorkspaceFilePart(part.url)
  );
  const textParts = message.parts.filter(isTextUIPart);

  return (
    <ChatBubble author="user">
      <Stack spacing={1}>
        {inlineFileParts.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap>
            {inlineFileParts.map(part => (
              <Box
                key={part.url.slice(0, 40)}
                component="img"
                src={part.url}
                alt={intl.formatMessage({
                  id: 'features.chat.message.attachedImage',
                })}
                sx={{
                  maxWidth: 200,
                  maxHeight: 150,
                  borderRadius: borderRadius('s'),
                  objectFit: 'contain',
                }}
              />
            ))}
          </Stack>
        )}

        {workspaceFileParts.length > 0 && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {workspaceFileParts.map(part => (
              <WorkspaceFileChip
                key={part.url}
                path={part.url.slice(WORKSPACE_FILE_PREFIX.length)}
              />
            ))}
          </Stack>
        )}

        {textParts.length > 0 && (
          <Stack direction="column" spacing={1} useFlexGap>
            {textParts.map(part => (
              <ChatMessageText key={part.text.slice(0, 40)} text={part.text} />
            ))}
          </Stack>
        )}
      </Stack>
    </ChatBubble>
  );
};

type AssistantMessageProps = {
  message?: ChatUIMessage;
  isStreaming?: boolean;
  onFeedback?: (isPositive: boolean) => void;
};

export const AssistantMessage = ({
  message,
  isStreaming = false,
  onFeedback,
}: AssistantMessageProps) => {
  const intl = useIntl();
  const metadata = getChatMetadata(message?.metadata);
  const parts = message?.parts ?? [];

  const reasoning = getReasoningContent(parts);
  const isReasoningRedacted = hasRedactedReasoning(parts);

  const textContent = getFinalTextContent(parts);
  const allToolParts = parts.filter(isToolUIPart);
  const hasToolParts = allToolParts.length > 0;

  const { filtered: displayContent, hasIncomplete: isGeneratingDiagram } =
    textContent
      ? processMermaidBlocks(textContent)
      : { filtered: '', hasIncomplete: false };

  const hasBody = Boolean(displayContent);

  // While streaming, show only a one-line status that swaps with each SSE
  // event; reasoning and tool steps appear once the message completes.
  const activity = isStreaming
    ? getAgentActivity(parts, intl, isGeneratingDiagram)
    : null;

  return (
    <Stack spacing={2}>
      {activity && <AgentActivityLabel label={activity} />}

      {!isStreaming && (
        <Stack spacing={0.5}>
          {(reasoning || isReasoningRedacted) && (
            <CollapsibleReasoning
              content={reasoning}
              isReasoningRedacted={isReasoningRedacted}
            />
          )}

          {hasToolParts && <ChatStepIndicator parts={parts} />}
        </Stack>
      )}

      {hasBody &&
        (isStreaming ? (
          <Box sx={{ display: 'inline' }}>
            <MarkdownRenderer content={displayContent} />
            <TypingCursor />
          </Box>
        ) : (
          <MarkdownRenderer content={displayContent} />
        ))}

      {!isStreaming && (onFeedback || hasBody) && (
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, px: 0.5 }}>
          {onFeedback &&
            [true, false].map(isPositive => (
              <FeedbackButton
                key={String(isPositive)}
                isPositive={isPositive}
                isSelected={metadata.feedback === isPositive}
                onClick={() => onFeedback(isPositive)}
              />
            ))}
          {hasBody && <CopyButton content={textContent} />}
        </Stack>
      )}
    </Stack>
  );
};

type ChatMessageProps = {
  message: ChatUIMessage;
  onFeedback?: (isPositive: boolean) => void;
};

export const ChatMessage = ({ message, onFeedback }: ChatMessageProps) =>
  message.role === 'user' ? (
    <UserMessage message={message} />
  ) : (
    <AssistantMessage message={message} onFeedback={onFeedback} />
  );
